/**
 * Client-side image compression & downscaling.
 *
 * Why: modern phone photos are 3–8 MB (regular) or 40–75 MB (ProRAW /
 * high-res DSLR). Sending them up as-is
 *   · is slow on cellular
 *   · often blows past Kubernetes ingress body-size defaults (typ. 20 MB)
 *   · costs Cloudinary bandwidth we never need (we redeliver at ≤1200px)
 *
 * `resizeImageForUpload(file, opts)` returns a Promise<File>:
 *   · non-images                                → unchanged
 *   · small enough images                       → unchanged
 *   · everything else                           → resampled to fit
 *     within `maxDim` on the longest edge, encoded as JPEG at `quality`.
 *
 * Design ruling 07 Jul 2026:
 *   · Never touch the pixels of anything ≤ maxSideEasy (default 1600px)
 *     AND ≤ softLimitBytes (default 2 MB). Feels lossless.
 *   · JPEG only (we never need PNG for photos; PNG stays PNG for logos
 *     because file.type won't be image/png in a photo picker context).
 *   · Falls back to the original file if anything in the canvas path
 *     throws — never breaks an upload just because we tried to help.
 */

const IMAGE_MIME_RE = /^image\/(jpeg|jpg|png|webp|heic|heif)$/i;

function loadImage(objectUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = objectUrl;
  });
}

export async function resizeImageForUpload(
  file,
  {
    maxDim = 2500,          // longest edge in px after resample
    quality = 0.85,         // JPEG quality (0..1)
    softLimitBytes = 2 * 1024 * 1024,  // ≤2MB → leave alone
    maxSideEasy = 1600,     // ≤this on longest edge → leave alone
  } = {},
) {
  if (!file || typeof file !== "object") return file;
  if (!IMAGE_MIME_RE.test(file.type || "")) return file;

  // HEIC/HEIF cannot be decoded by canvas in most browsers. Fall through
  // untouched — the backend accepts them and Cloudinary transcodes.
  if (/heic|heif/i.test(file.type)) return file;

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const srcW = img.naturalWidth;
    const srcH = img.naturalHeight;
    const longest = Math.max(srcW, srcH);

    // Cheap-out: small enough on both axes AND file → don't reencode.
    if (longest <= maxSideEasy && file.size <= softLimitBytes) return file;

    const scale = longest > maxDim ? maxDim / longest : 1;
    const outW = Math.round(srcW * scale);
    const outH = Math.round(srcH * scale);

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    // Better resample on Chromium/WebKit.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, outW, outH);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return file;

    // Guard: if our "compressed" output is somehow larger than the
    // original (rare · e.g. tiny PNG icon fed here by mistake), keep
    // the original.
    if (blob.size >= file.size) return file;

    const baseName = (file.name || "photo").replace(/\.(jpe?g|png|webp)$/i, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Convenience wrapper for photo-picker onChange handlers · resamples
 * each file in parallel and returns a File[] in original order.
 */
export function resizeImagesForUpload(files, opts) {
  const arr = Array.from(files || []);
  return Promise.all(arr.map((f) => resizeImageForUpload(f, opts)));
}
